// ABOUTME: Landing page for root path with market selection
// ABOUTME: Shows cyberpunk-styled interface for choosing between Premium and Legacy markets
import { Link as RouterLink } from "react-router-dom";
import { ShoppingCart, Shield, AlertCircle, ArrowRight, Key, ChevronRight } from "lucide-react";

export function LandingPage() {
  const markets = [
    {
      title: "Legacy Market",
      description: "Sign in with Azure AD B2C - Start here for migration demo",
      icon: ShoppingCart,
      path: "/legacy-market",
      color: "from-gray-600 to-gray-800",
      borderColor: "border-gray-500",
      textColor: "text-gray-300",
      accentColor: "text-gray-400",
      features: ["Azure AD B2C", "Classic Authentication", "Step 1: Sign in here first"],
      badge: "START HERE",
      badgeColor: "from-gray-500 to-gray-700",
      order: 1,
      isLegacy: true
    },
    {
      title: "Premium Market",
      description: "Sign in with Entra External ID - Use same password after legacy login",
      icon: Shield,
      path: "/premium-market",
      color: "from-blue-500 to-purple-600",
      borderColor: "border-blue-500",
      textColor: "text-yellow-400",
      accentColor: "text-green-400",
      features: ["Entra External ID", "Just-in-Time Migration", "Step 2: Use same credentials"],
      badge: "THEN HERE",
      badgeColor: "from-yellow-400 to-red-500",
      order: 2,
      isLegacy: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-blue-900 to-black overflow-hidden relative">
      {/* Animated grid background */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iODAiIGhlaWdodD0iODAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gODAgMCBMIDAgMCAwIDgwIiBmaWxsPSJub25lIiBzdHJva2U9IiMzOTM5NzkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIiAvPjwvc3ZnPg==')] opacity-30 animate-pulse"></div>

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="flex flex-col items-center">
          {/* Demo Notice Banner */}
          <div className="w-full max-w-6xl mb-8">
            <div className="bg-yellow-900/30 backdrop-blur-sm p-6 rounded-lg border-2 border-yellow-400 glow-yellow">
              <div className="flex items-start">
                <AlertCircle className="w-6 h-6 text-yellow-400 mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-yellow-400 font-bold font-mono text-lg mb-2">
                    🎮 DEMO ENVIRONMENT - MIGRATION SHOWCASE
                  </h3>
                  <p className="text-yellow-200 font-mono text-sm">
                    This is a demonstration of Azure AD B2C to Entra External ID migration capabilities
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-4 text-gradient-cyberpunk tracking-tight">
              MARKET PORTAL
            </h1>
            <p className="text-blue-300 text-xl md:text-2xl tracking-widest mb-4">
              MIGRATION DEMO
            </p>
          </div>

          {/* Migration Explanation Section */}
          <div className="w-full max-w-6xl mb-12">
            <div className="bg-black/70 backdrop-blur-sm p-8 rounded-lg border-2 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.5)]">
              <div className="flex items-center mb-6">
                <Key className="w-10 h-10 text-purple-400 mr-4" />
                <h2 className="text-3xl font-bold text-purple-400 font-mono">
                  JUST-IN-TIME MIGRATION DEMO
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gray-900/30 p-4 rounded-lg border border-gray-500/50">
                  <div className="text-gray-300 font-mono font-bold mb-2">STEP 1: LEGACY (OLD SYSTEM)</div>
                  <p className="text-gray-400 text-sm font-mono">
                    Sign in to Legacy Market using Azure AD B2C credentials - Black & White interface
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <ArrowRight className="w-8 h-8 text-yellow-400 animate-pulse" />
                </div>

                <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-500/50">
                  <div className="text-blue-400 font-mono font-bold mb-2">STEP 2: PREMIUM (NEW SYSTEM)</div>
                  <p className="text-blue-300 text-sm font-mono">
                    Sign in to Premium Market with same password - Colorful modern interface
                  </p>
                </div>
              </div>

              <div className="bg-purple-900/30 p-4 rounded-lg border border-purple-500/50">
                <h3 className="text-purple-400 font-mono font-bold mb-2 flex items-center">
                  <Key className="w-4 h-4 mr-2" />
                  KEY FEATURE: Password Migration
                </h3>
                <p className="text-blue-300 text-sm font-mono">
                  During sign-in to Premium Market, your password is validated against Azure AD B2C and seamlessly migrated to Entra External ID.
                  After migration, you can use the same credentials on the new platform without any disruption.
                </p>
              </div>
            </div>
          </div>

          {/* Market Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl w-full">
            {markets.map((market, index) => {
              const IconComponent = market.icon;
              return (
                <RouterLink
                  key={index}
                  to={market.path}
                  className={`block bg-black/70 backdrop-blur-sm p-8 rounded-lg border-2 ${market.borderColor} ${market.isLegacy ? 'grayscale hover:border-gray-400' : 'glow-blue hover:border-yellow-400'} transition-all duration-300 group hover:transform hover:scale-105 relative`}
                >
                  {/* Step Badge */}
                  <div className={`absolute -top-3 -right-3 bg-gradient-to-r ${market.badgeColor} ${market.isLegacy ? 'text-white' : 'text-black'} font-bold font-mono text-xs px-4 py-2 rounded-full shadow-lg animate-pulse`}>
                    {market.badge}
                  </div>

                  {/* Legacy System Badge */}
                  {market.isLegacy && (
                    <div className="absolute top-4 right-4 bg-gray-800 px-2 py-1 rounded border border-gray-600">
                      <span className="text-gray-400 font-mono text-xs">OLD SYSTEM</span>
                    </div>
                  )}

                  <div className="flex items-center mb-6">
                    <div className={`p-4 rounded-lg bg-gradient-to-r ${market.color} mr-4`}>
                      <IconComponent className="w-12 h-12 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${market.textColor} font-mono`}>
                        {market.title}
                      </h2>
                      <div className={`text-xs ${market.accentColor} font-mono mt-1`}>
                        STEP {market.order}
                      </div>
                    </div>
                  </div>

                  <p className={`${market.isLegacy ? 'text-gray-400' : 'text-blue-300'} mb-6 font-mono text-sm`}>
                    {market.description}
                  </p>

                  <div className="mb-6">
                    <h3 className={`${market.accentColor} font-mono text-sm mb-3`}>FEATURES:</h3>
                    <ul className="space-y-2">
                      {market.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className={`${market.isLegacy ? 'text-gray-500' : 'text-blue-400'} text-xs font-mono flex items-center`}>
                          <span className={`w-2 h-2 ${market.isLegacy ? 'bg-gray-500' : 'bg-green-400'} rounded-full mr-2`}></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Demo Hint */}
                  {market.order === 1 && (
                    <div className="mb-4 p-3 bg-gray-900/20 rounded border border-gray-500/30">
                      <p className="text-gray-400 text-xs font-mono">
                        💡 DEMO HINT: Black & White interface represents the old system
                      </p>
                    </div>
                  )}

                  {market.order === 2 && (
                    <div className="mb-4 p-3 bg-blue-900/20 rounded border border-blue-500/30">
                      <p className="text-blue-400 text-xs font-mono">
                        💡 DEMO HINT: Use the SAME password as Legacy Market - it will migrate automatically!
                      </p>
                    </div>
                  )}

                  <div className={`flex items-center ${market.isLegacy ? 'text-gray-300' : 'text-yellow-400'} font-mono group-hover:${market.isLegacy ? 'text-gray-200' : 'text-yellow-300'}`}>
                    <span>ACCESS MARKET</span>
                    <ChevronRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </RouterLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* Animated light streaks */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-70"
            style={{
              top: `${Math.random() * 100}%`,
              left: 0,
              right: 0,
              animation: `lightStreak ${3 + Math.random() * 7}s linear infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      <style jsx>{`
        @keyframes lightStreak {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
